/**
 * BoardingPassClearanceMobile.tsx — Security Checkpoint CAPTCHA (mobile)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TASK 80 — MOBILE-FIRST CAPTCHA REDESIGN
 *
 * This is NOT a responsive layout. It is a standalone CAPTCHA-style
 * verification challenge — the same mental model as Google image CAPTCHA
 * or Cloudflare Turnstile, but travel themed.
 *
 * Visual hierarchy (corrected per operator mandate):
 *   1. Essential Items  (3fr — LARGEST)   — the CAPTCHA tiles users act on
 *   2. Backpack         (2fr — SECOND)     — the destination, ~68% screen width
 *   3. Leave Behind     (1fr — SMALLEST)   — distractor set, visually compressed
 *
 * CSS Grid layout — 6 rows, height: 100dvh, overflow: hidden, zero scroll:
 *   44px  — Security checkpoint header
 *   26px  — Single dynamic instruction line
 *   3fr   — Take With You: 3×2 full-bleed CAPTCHA tiles
 *   2fr   — Backpack: airport bg + large hero + tight drop zone (middle)
 *   3fr   — Leave Behind: 3×2 tiles — SAME tile size as Take With You
 *   50px  — Progress bar + COMPLETE CHECK action
 *
 * Game logic: shared via useBoardingPassGame() hook.
 * This component owns only its own JSX and CSS — zero game logic inside.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Lock } from 'lucide-react';
import { ALL_ITEMS } from './boardingPassItems';
import type { Item } from './boardingPassItems';
import { useBoardingPassGame } from './useBoardingPassGame';

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  onVerified: () => void;
  onError: (msg: string) => void;
  onClose: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// CSS — keyframes + CAPTCHA tile classes
// Defined here so this component is fully self-contained.
// ─────────────────────────────────────────────────────────────────────────────

const CAPTCHA_CSS = `
  @keyframes wr-shake {
    0%,100%{transform:translateX(0) rotate(0)}
    14%{transform:translateX(-10px) rotate(-2deg)}
    28%{transform:translateX(10px) rotate(2deg)}
    42%{transform:translateX(-8px) rotate(-1.5deg)}
    57%{transform:translateX(8px) rotate(1.5deg)}
    71%{transform:translateX(-4px) rotate(0)}
    85%{transform:translateX(4px) rotate(0)}
  }
  @keyframes wr-snap {
    0%{transform:scale(1.25);opacity:0}
    60%{transform:scale(0.97)}
    100%{transform:scale(1);opacity:1}
  }
  @keyframes wr-pulse-teal {
    0%,100%{border-color:rgba(0,200,180,0.32);box-shadow:inset 0 0 12px rgba(0,180,160,0.04)}
    50%{border-color:rgba(0,220,200,0.72);box-shadow:inset 0 0 22px rgba(0,200,180,0.1),0 0 16px rgba(0,200,180,0.12)}
  }
  @keyframes wr-pulse-gold {
    0%,100%{border-color:rgba(250,204,21,0.5);box-shadow:0 0 0 1px rgba(250,204,21,0.12)}
    50%{border-color:rgba(250,204,21,0.9);box-shadow:0 0 0 3px rgba(250,204,21,0.18)}
  }
  @keyframes wr-badge-pop {
    0%{transform:scale(0) rotate(-10deg);opacity:0}
    70%{transform:scale(1.15) rotate(3deg)}
    100%{transform:scale(1) rotate(0);opacity:1}
  }
  @keyframes wr-wrong-msg {
    0%{opacity:0;transform:translate(-50%,-50%) scale(0.8)}
    15%{opacity:1;transform:translate(-50%,-50%) scale(1.02)}
    85%{opacity:1;transform:translate(-50%,-50%) scale(1)}
    100%{opacity:0;transform:translate(-50%,-50%) scale(0.95)}
  }

  /* ── Premium interaction animations ─────────────────────────────────────── */

  /* Tap feedback — brief press-in scale */
  @keyframes wr-tile-press {
    0%   { transform: scale(1); }
    40%  { transform: scale(0.94); }
    100% { transform: scale(1); }
  }
  /* Pack: tile flies toward backpack and vanishes */
  @keyframes wr-tile-fly {
    0%   { transform: scale(1);             opacity: 1; }
    55%  { transform: scale(0.72) translateY(-6px); opacity: 0.65; }
    100% { transform: scale(0.28) translateY(-14px); opacity: 0; }
  }
  /* Wrong item: red overlay fades out */
  @keyframes wr-wrong-flash {
    0%   { opacity: 0.75; }
    100% { opacity: 0; }
  }
  /* Backpack: gentle idle breathing via filter */
  @keyframes wr-backpack-breathe {
    0%,100% { filter: drop-shadow(0 6px 22px rgba(10,80,180,0.42)) brightness(0.91); }
    50%     { filter: drop-shadow(0 8px 34px rgba(30,110,230,0.68)) brightness(0.97); }
  }
  /* Backpack: quick pulse when item packed */
  @keyframes wr-backpack-pulse {
    0%   { transform: scale(1); }
    28%  { transform: scale(1.10); }
    58%  { transform: scale(0.97); }
    80%  { transform: scale(1.02); }
    100% { transform: scale(1); }
  }
  /* Backpack: zip-close bounce on completion */
  @keyframes wr-success-zip {
    0%   { transform: scale(1) rotate(0deg); }
    18%  { transform: scale(1.13) rotate(-2deg); }
    40%  { transform: scale(0.93) rotate(1.5deg); }
    65%  { transform: scale(1.05) rotate(-0.5deg); }
    100% { transform: scale(1) rotate(0deg); }
  }
  /* Counter: pop on increment */
  @keyframes wr-counter-bump {
    0%   { transform: scale(1); }
    35%  { transform: scale(1.55); }
    65%  { transform: scale(0.92); }
    100% { transform: scale(1); }
  }

  /* CAPTCHA tile — full-bleed image with gradient label overlay */
  .wr-mc-tile {
    position: relative;
    overflow: hidden;
    border-radius: 7px;
    touch-action: none;
    user-select: none;
    -webkit-user-select: none;
    will-change: transform, opacity;
  }
  .wr-mc-tile-img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    user-select: none;
    -webkit-user-select: none;
    pointer-events: none;
  }
  .wr-mc-tile-emoji {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    user-select: none;
  }
  .wr-mc-tile-label {
    position: absolute;
    bottom: 0; left: 0; right: 0;
    padding: 10px 4px 5px;
    background: linear-gradient(0deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.4) 55%, transparent 100%);
    font-family: monospace;
    font-size: 7.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: rgba(190,210,245,0.92);
    text-align: center;
    line-height: 1;
    pointer-events: none;
  }
  /* Wrong tile: red tint overlay to visually de-emphasize */
  .wr-mc-tile-wrong-tint {
    position: absolute;
    inset: 0;
    background: rgba(110,15,15,0.22);
    pointer-events: none;
  }
  .wr-mc-tile-packed {
    position: absolute;
    inset: 0;
    background: rgba(34,197,94,0.22);
    display: flex;
    align-items: center;
    justify-content: center;
    animation: wr-snap 0.4s ease;
    pointer-events: none;
  }
  /* Wrong item: bright red flash overlay, animates out */
  .wr-mc-tile-wrong-flash {
    position: absolute;
    inset: 0;
    background: rgba(239,68,68,0.52);
    pointer-events: none;
    animation: wr-wrong-flash 0.5s ease-out forwards;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// CaptchaTileImage — full-bleed image with emoji fallback
// ─────────────────────────────────────────────────────────────────────────────

function CaptchaTileImage({ item }: { item: Item }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className="wr-mc-tile-emoji"
        style={{ background: item.correct ? 'rgba(10,25,70,0.9)' : 'rgba(50,8,8,0.9)' }}
      >
        {item.emoji}
      </div>
    );
  }
  return (
    <img
      src={item.imgUrl}
      alt={item.label}
      draggable={false}
      onError={() => setFailed(true)}
      className="wr-mc-tile-img"
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main mobile CAPTCHA component
// ─────────────────────────────────────────────────────────────────────────────

export default function BoardingPassClearanceMobile({ onVerified, onError, onClose: _onClose }: Props) {

  // Shuffle all 12 items once per mount — different order every session.
  // Fisher-Yates in-place on a copy so the catalog arrays stay unchanged.
  const shuffledItems = useMemo(() => {
    const arr = [...ALL_ITEMS];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, []);
  const topItems    = shuffledItems.slice(0, 6);
  const bottomItems = shuffledItems.slice(6);

  // All game logic from the shared hook — zero game state declared here
  const {
    phase, count, packedDisplay, shakenItem, wrongMsg,
    isDragOver, backpackGlow, draggedId, selectedId,
    dropZoneRef, handlePointerDown, handlePointerMove,
    handlePointerUp, handleBackpackClick,
  } = useBoardingPassGame({ onVerified, onError });

  // ── Animation-only state (no game logic) ─────────────────────────────── //
  const [pressedId,       setPressedId]       = useState<string | null>(null);
  const [justPackedId,    setJustPackedId]    = useState<string | null>(null);
  const [wrongFlashId,    setWrongFlashId]    = useState<string | null>(null);
  const [backpackPulsing, setBackpackPulsing] = useState(false);
  const prevPackedRef = useRef<Set<string>>(new Set());

  // Detect newly packed item → fly animation + backpack pulse + counter bump
  useEffect(() => {
    const prev = prevPackedRef.current;
    let newId: string | null = null;
    for (const id of packedDisplay) {
      if (!prev.has(id)) { newId = id; break; }
    }
    if (newId) {
      setJustPackedId(newId);
      setBackpackPulsing(true);
      setTimeout(() => setJustPackedId(null), 290);
      setTimeout(() => setBackpackPulsing(false), 480);
    }
    prevPackedRef.current = new Set(packedDisplay);
  }, [packedDisplay]);

  // Wrong item → red flash + haptic vibration
  useEffect(() => {
    if (!shakenItem) return;
    setWrongFlashId(shakenItem);
    try { navigator.vibrate?.(55); } catch { /* ignore — not supported everywhere */ }
    setTimeout(() => setWrongFlashId(null), 520);
  }, [shakenItem]);

  // Wrapped pointer-down: add tap-press animation on top of hook handler
  const wrappedPointerDown = useCallback((e: React.PointerEvent, itemId: string) => {
    handlePointerDown(e, itemId);
    if (!packedDisplay.has(itemId) && phase === 'puzzle') {
      setPressedId(itemId);
      setTimeout(() => setPressedId(null), 160);
    }
  }, [handlePointerDown, packedDisplay, phase]);

  // ── Computed: tile border / glow style ─────────────────────────────────── //
  const tileStyle = (item: Item): React.CSSProperties => ({
    border: packedDisplay.has(item.id)
      ? '2px solid rgba(34,197,94,0.72)'
      : selectedId === item.id
        ? '2.5px solid rgba(0,220,200,0.95)'
        : '1.5px solid rgba(30,80,180,0.25)',
    boxShadow: selectedId === item.id
      ? '0 0 0 3px rgba(0,220,200,0.22), inset 0 0 22px rgba(0,220,200,0.08), 0 4px 16px rgba(0,0,0,0.5)'
      : packedDisplay.has(item.id)
        ? '0 0 8px rgba(34,197,94,0.18)'
        : 'none',
    cursor: packedDisplay.has(item.id) || phase !== 'puzzle' ? 'default' : 'pointer',
    // During fly animation, keep opacity at 1 so the keyframe controls it.
    // After fly (~290ms), opacity transitions via the CSS transition to 0.32.
    opacity: justPackedId === item.id
      ? 1
      : packedDisplay.has(item.id) ? 0.32 : draggedId === item.id ? 0.38 : 1,
    transition: 'border-color 0.18s, box-shadow 0.18s, opacity 0.3s',
    // Animation priority: shake > fly > press > idle
    animation: shakenItem === item.id
      ? 'wr-shake 0.65s ease'
      : justPackedId === item.id
        ? 'wr-tile-fly 0.32s ease-out forwards'
        : pressedId === item.id
          ? 'wr-tile-press 0.16s ease-out'
          : 'none',
  });

  // ── Computed: drop-zone border / background ────────────────────────────── //
  const dropZoneStyle: React.CSSProperties = {
    border: isDragOver
      ? '2px dashed rgba(0,220,200,0.95)'
      : (phase === 'success' || phase === 'verifying')
        ? '2px solid rgba(34,197,94,0.9)'
        : selectedId
          ? '2px dashed rgba(250,204,21,0.78)'
          : '1.5px dashed rgba(0,200,185,0.38)',
    background: isDragOver
      ? 'rgba(0,210,185,0.09)'
      : (phase === 'success' || phase === 'verifying')
        ? 'rgba(34,197,94,0.07)'
        : 'rgba(0,0,0,0.08)',
    cursor: selectedId ? 'pointer' : 'default',
    animation: (phase === 'puzzle' && count === 0 && !isDragOver)
      ? 'wr-pulse-teal 2.8s ease-in-out infinite'
      : selectedId
        ? 'wr-pulse-gold 1.0s ease-in-out infinite'
        : 'none',
    borderRadius: 12,
    transition: 'border 0.18s, background 0.18s',
  };

  // ── Computed: backpack image filter ───────────────────────────────────── //
  // When idle (no drag, no glow, puzzle phase), the CSS breathing animation
  // controls the filter via keyframes. Otherwise apply the computed filter.
  const isBackpackIdle = !isDragOver && !backpackGlow && phase === 'puzzle';
  const backpackFilter = isDragOver
    ? 'drop-shadow(0 0 28px rgba(0,220,190,0.8)) brightness(1.08)'
    : backpackGlow
      ? 'drop-shadow(0 0 22px rgba(34,197,94,0.85)) brightness(1.05)'
      : 'drop-shadow(0 6px 22px rgba(10,80,180,0.45)) brightness(0.92)';

  // dropZoneContent removed — overlay is rendered inline in JSX below

  // ── Wrong message overlay ─────────────────────────────────────────────── //
  const wrongMsgOverlay = wrongMsg ? (
    <div style={{
      position: 'fixed', top: '50%', left: '50%', zIndex: 10001,
      transform: 'translate(-50%, -50%)',
      background: 'rgba(100,22,22,0.97)',
      border: '1.5px solid rgba(239,68,68,0.7)',
      borderRadius: 12, padding: '12px 22px',
      fontFamily: 'monospace', fontSize: 12, fontWeight: 700,
      color: '#fca5a5', pointerEvents: 'none',
      boxShadow: '0 12px 48px rgba(0,0,0,0.7), 0 0 24px rgba(239,68,68,0.22)',
      animation: 'wr-wrong-msg 1.5s ease forwards',
      whiteSpace: 'nowrap',
    }}>
      ✗ &nbsp;{wrongMsg}
    </div>
  ) : null;

  // ── Dynamic instruction text ──────────────────────────────────────────── //
  const instructionText = (phase === 'success' || phase === 'verifying')
    ? (phase === 'verifying' ? '⟳  VERIFYING YOUR IDENTITY...' : '✓  BOARDING PASS CLEARED!')
    : selectedId
      ? `✦  ${ALL_ITEMS.find(i => i.id === selectedId)?.label ?? ''} selected — tap backpack to pack`
      : 'Pack the six travel essentials.';

  const instructionColor = (phase === 'success' || phase === 'verifying')
    ? '#22c55e'
    : selectedId ? '#facc15' : '#00c8c8';

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // Six-row CSS Grid — 100dvh, overflow: hidden, zero scroll.
  // Row proportions enforce visual hierarchy: 3fr > 2fr > 1fr.
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      height: '100dvh',
      display: 'grid',
      gridTemplateRows: '44px 26px 3fr 2fr 3fr 50px',
      overflow: 'hidden',
      background: 'linear-gradient(180deg, #060d22 0%, #040912 100%)',
      fontFamily: "'Inter','Segoe UI',system-ui,sans-serif",
    }}>
      <style>{CAPTCHA_CSS}</style>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* ROW 1 — Security checkpoint header (44px)                        */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 13px',
        background: 'rgba(2,6,18,0.95)',
        borderBottom: '1px solid rgba(20,60,150,0.2)',
        flexShrink: 0,
      }}>
        {/* Left: step pill */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
          <Lock size={10} color="#3b82f6" />
          <span style={{
            fontFamily: 'monospace', fontSize: 8.5, fontWeight: 700,
            letterSpacing: '0.14em', color: '#3b82f6',
          }}>
            STEP 2 OF 3
          </span>
        </div>
        {/* Center: checkpoint label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 22, height: 1, background: 'linear-gradient(90deg,transparent,rgba(0,200,200,0.45))' }} />
          <span style={{
            fontFamily: 'monospace', fontSize: 8.5, fontWeight: 700,
            letterSpacing: '0.17em', color: '#00c8c8', whiteSpace: 'nowrap',
          }}>
            ⊕ SECURITY CHECKPOINT ⊕
          </span>
          <div style={{ width: 22, height: 1, background: 'linear-gradient(90deg,rgba(0,200,200,0.45),transparent)' }} />
        </div>
        {/* Right: step progress dots */}
        <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
          {[0,1,2].map(i => (
            <div key={i} style={{
              width: 15, height: 4, borderRadius: 2,
              background: i === 1 ? '#3b82f6' : 'rgba(59,130,246,0.13)',
              boxShadow: i === 1 ? '0 0 6px rgba(59,130,246,0.55)' : 'none',
            }} />
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* ROW 2 — Single instruction line (26px)                           */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.16)',
        borderBottom: '1px solid rgba(0,200,185,0.07)',
        padding: '0 14px',
        flexShrink: 0,
      }}>
        <span style={{
          fontFamily: 'monospace', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.06em', textAlign: 'center',
          color: instructionColor,
          transition: 'color 0.2s',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          maxWidth: '100%',
        }}>
          {instructionText}
        </span>
      </div>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* ROW 3 — TAKE WITH YOU: 3×2 CAPTCHA tile grid (3fr — LARGEST)     */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <div style={{
        display: 'flex', flexDirection: 'column',
        padding: '6px 10px 4px',
        minHeight: 0, overflow: 'hidden',
      }}>
        {/* Count badge only — no label that hints correct vs incorrect */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4, flexShrink: 0 }}>
          <span style={{
            fontFamily: 'monospace', fontSize: 10, fontWeight: 900,
            color: count === 6 ? '#22c55e' : count > 0 ? '#3a6040' : '#1a3025',
          }}>
            {count}/6
          </span>
        </div>

        {/* 3×2 CAPTCHA tile grid — filled with randomly shuffled items */}
        <div style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gridTemplateRows: '1fr 1fr',
          gap: 5,
          minHeight: 0,
        }}>
          {topItems.map(item => (
            <div
              key={item.id}
              className="wr-mc-tile"
              style={tileStyle(item)}
              onPointerDown={e => wrappedPointerDown(e, item.id)}
              onPointerMove={e => handlePointerMove(e, item.id)}
              onPointerUp={e => handlePointerUp(e, item.id)}
              onPointerCancel={e => handlePointerUp(e, item.id)}
            >
              <CaptchaTileImage item={item} />
              <div className="wr-mc-tile-label">{item.label}</div>
              {packedDisplay.has(item.id) && (
                <div className="wr-mc-tile-packed">
                  <span style={{ fontSize: 20, color: '#22c55e', textShadow: '0 0 12px rgba(34,197,94,0.8)' }}>✓</span>
                </div>
              )}
              {wrongFlashId === item.id && <div className="wr-mc-tile-wrong-flash" />}
            </div>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* ROW 4 — BACKPACK: airport bg + large hero + tight drop zone (2fr) */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <div style={{ position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>

        {/* Airport terminal background */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: "url('https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=900&h=400&fit=crop&auto=format&q=50')",
          backgroundSize: 'cover', backgroundPosition: 'center',
          filter: 'brightness(0.16) saturate(0.6)',
        }} />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse 85% 75% at 50% 50%, rgba(8,25,75,0.55) 0%, rgba(2,6,18,0.88) 100%)',
          pointerEvents: 'none',
        }} />

        {/* Tiny instruction label — responds to selection state */}
        <div style={{
          position: 'absolute', top: 6, left: 0, right: 0,
          textAlign: 'center', zIndex: 4,
          fontFamily: 'monospace', fontSize: 7.5, fontWeight: 700,
          letterSpacing: '0.13em',
          color: selectedId ? '#fbbf24' : '#00c8c8',
          pointerEvents: 'none',
          transition: 'color 0.18s',
        }}>
          {selectedId ? 'TAP BACKPACK TO PACK' : 'TAP ITEM  ·  TAP BACKPACK TO PACK'}
        </div>

        {/*
          BackpackWrapper — position: relative, display: inline-block.
          Centered via absolute + transform so it is never a flex item
          and always sizes to exactly the rendered image dimensions.
          The DropZoneOverlay is a direct child and therefore positioned
          relative to the image, not the section.
        */}
        <div style={{
          position: 'relative',
          display: 'inline-block',
          lineHeight: 0,
          zIndex: 2,
          // Wrapper animates transform — pulse on pack, zip on complete
          animation: (phase === 'success' || phase === 'verifying')
            ? 'wr-success-zip 0.65s cubic-bezier(0.36,0.07,0.19,0.97)'
            : backpackPulsing
              ? 'wr-backpack-pulse 0.48s cubic-bezier(0.36,0.07,0.19,0.97)'
              : 'none',
          willChange: 'transform',
        }}>
          <img
            src="/items/backpack_hero.svg"
            alt="Backpack"
            draggable={false}
            onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            style={{
              display: 'block',
              width: '110px',   /* operator spec: ~110px wide, centered  */
              height: 'auto',   /* aspect-ratio preserved automatically  */
              userSelect: 'none',
              // When idle: CSS keyframe controls filter for breathing.
              // When active: apply computed filter with transition.
              filter: isBackpackIdle ? undefined : backpackFilter,
              animation: isBackpackIdle ? 'wr-backpack-breathe 3.6s ease-in-out infinite' : 'none',
              transition: isBackpackIdle ? 'none' : 'filter 0.25s',
            }}
          />

          {/* ── Drop zone overlay — flex column, no fixed height ── */}
          <div
            ref={dropZoneRef}
            onClick={handleBackpackClick}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '68%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 14,
              ...dropZoneStyle,
              padding: '10px 6px',
            }}
          >
            {(phase === 'success' || phase === 'verifying') ? (
              <>
                <div style={{ fontSize: 18, textAlign: 'center', filter: 'drop-shadow(0 0 14px rgba(34,197,94,0.9))', animation: 'wr-badge-pop 0.5s ease' }}>
                  {phase === 'verifying' ? '⟳' : '✓'}
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: 9, fontWeight: 700, color: '#22c55e', letterSpacing: '0.1em', textAlign: 'center' }}>
                  {phase === 'verifying' ? 'VERIFYING…' : 'CLEARED'}
                </div>
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke={isDragOver ? 'rgba(0,220,200,0.95)' : selectedId ? '#fbbf24' : '#ffffff'}
                  strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                <div style={{ fontFamily: 'monospace', fontSize: 9, fontWeight: 700, color: '#ffffff', textAlign: 'center', whiteSpace: 'nowrap' }}>
                  DROP ITEMS HERE
                </div>
                <div
                  key={`counter-${count}`}
                  style={{
                    fontFamily: 'monospace', fontSize: 9, fontWeight: 600,
                    color: '#ffffff', textAlign: 'center', whiteSpace: 'nowrap',
                    animation: count > 0 ? 'wr-counter-bump 0.36s cubic-bezier(0.36,0.07,0.19,0.97)' : 'none',
                    willChange: 'transform',
                  }}
                >
                  {count} / 6 PACKED
                </div>
              </>
            )}
          </div>
        </div>

      </div>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* ROW 5 — LEAVE BEHIND: 3×2 grid (3fr — SAME tile size as top)     */}
      {/* Identical grid structure to Take With You. Red tint + red border  */}
      {/* signals "wrong". Backpack in the middle is the sorting boundary.  */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <div style={{
        display: 'flex', flexDirection: 'column',
        padding: '4px 10px 5px',
        minHeight: 0, overflow: 'hidden',
      }}>
        {/* 3×2 grid — remaining shuffled items, no label or colour hints */}
        <div style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gridTemplateRows: 'repeat(2, 1fr)',
          gap: 4,
          minHeight: 0,
        }}>
          {bottomItems.map(item => (
            <div
              key={item.id}
              className="wr-mc-tile"
              style={tileStyle(item)}
              onPointerDown={e => wrappedPointerDown(e, item.id)}
              onPointerMove={e => handlePointerMove(e, item.id)}
              onPointerUp={e => handlePointerUp(e, item.id)}
              onPointerCancel={e => handlePointerUp(e, item.id)}
            >
              <CaptchaTileImage item={item} />
              <div className="wr-mc-tile-label">{item.label}</div>
              {wrongFlashId === item.id && <div className="wr-mc-tile-wrong-flash" />}
            </div>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* ROW 6 — Action bar: progress segments + count + COMPLETE (50px)  */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 11, padding: '0 12px',
        background: 'rgba(2,5,16,0.98)',
        borderTop: '1px solid rgba(25,65,150,0.28)',
        flexShrink: 0,
      }}>
        {/* 6-segment progress track */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
          {[0,1,2,3,4,5].map(i => (
            <div key={i} style={{
              flex: 1, height: 6, borderRadius: 3,
              background: i < count ? '#22c55e' : 'rgba(34,197,94,0.08)',
              border: `1px solid ${i < count ? 'rgba(34,197,94,0.55)' : 'rgba(34,197,94,0.1)'}`,
              boxShadow: i < count ? '0 0 5px rgba(34,197,94,0.35)' : 'none',
              transition: 'background 0.25s, box-shadow 0.25s',
            }} />
          ))}
          <span style={{
            fontFamily: 'monospace', fontSize: 12, fontWeight: 900,
            color: count === 6 ? '#22c55e' : '#1e3248',
            marginLeft: 5, flexShrink: 0, transition: 'color 0.3s',
          }}>
            {count}/6
          </span>
        </div>

        {/* Continue / COMPLETE button */}
        <div style={{
          flexShrink: 0, padding: '9px 16px', borderRadius: 9,
          background: count === 6
            ? 'linear-gradient(135deg,rgba(18,90,46,0.97),rgba(22,140,65,0.97))'
            : 'rgba(8,15,36,0.7)',
          border: `1.5px solid ${count === 6 ? 'rgba(34,197,94,0.65)' : 'rgba(25,60,120,0.22)'}`,
          boxShadow: count === 6 ? '0 0 16px rgba(34,197,94,0.22)' : 'none',
          transition: 'all 0.3s',
        }}>
          <span style={{
            fontFamily: 'monospace', fontSize: 10, fontWeight: 900,
            letterSpacing: '0.1em', whiteSpace: 'nowrap',
            color: count === 6 ? '#4ade80' : '#162034',
          }}>
            {count === 6 ? '✓  CONTINUE' : 'PACK ALL 6'}
          </span>
        </div>
      </div>

      {/* Wrong message overlay */}
      {wrongMsgOverlay}

    </div>
  );
}
